import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  Copy, 
  Download,
  Eye,
  EyeOff,
  Lock
} from 'lucide-react';

const TwoFactorAuthSetup = ({ apiCall, token, user, onSuccess, onCancel, toast }) => {
  const [step, setStep] = useState('password'); // password, qr, verify, complete
  const [password, setPassword] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savedBackupCodes, setSavedBackupCodes] = useState(false);

  // Step 1: Verify password and initiate 2FA setup
  const handleInitiate2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiCall('/auth/2fa/enable', 'POST', { password }, token);
      
      if (response.success && response.data) {
        setQrCode(response.data.qr_code);
        setManualKey(response.data.manual_entry_key);
        setBackupCodes(response.data.backup_codes || []);
        setStep('qr');
        toast?.success('2FA setup initiated! Scan the QR code with your authenticator app.');
      } else {
        setError(response.data?.detail || 'Failed to initiate 2FA setup');
      }
    } catch (err) {
      setError(err.message || 'Error initiating 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify TOTP code from authenticator app
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiCall('/auth/2fa/verify', 'POST', { 
        token: verificationCode 
      }, token);
      
      if (response.success) {
        setStep('complete');
        toast?.success('✅ 2FA enabled successfully!');
        
        // Auto-close after 3 seconds
        setTimeout(() => {
          onSuccess?.();
        }, 3000);
      } else {
        setError(response.data?.detail || 'Invalid verification code');
      }
    } catch (err) {
      setError(err.message || 'Error verifying 2FA code');
    } finally {
      setLoading(false);
    }
  };

  // Copy backup codes to clipboard
  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast?.success('Backup codes copied to clipboard!');
  };

  // Download backup codes as text file
  const downloadBackupCodes = () => {
    const codesText = `CounselCore 2FA Backup Codes\nUser: ${user?.email}\nGenerated: ${new Date().toLocaleString()}\n\n${backupCodes.join('\n')}\n\n⚠️ Keep these codes safe! Each can only be used once.`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `counselcore-2fa-backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setSavedBackupCodes(true);
    toast?.success('Backup codes downloaded!');
  };

  // Copy manual entry key
  const copyManualKey = () => {
    navigator.clipboard.writeText(manualKey);
    toast?.success('Manual entry key copied!');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-emerald-600" />
              <div>
                <CardTitle>Two-Factor Authentication Setup</CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </div>
            </div>
            {onCancel && (
              <Button variant="ghost" size="sm" onClick={onCancel}>
                ✕
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-6">
            <div className={`flex items-center gap-2 ${step === 'password' ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'password' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100'}`}>
                1
              </div>
              <span className="text-sm font-medium">Verify</span>
            </div>
            <div className="flex-1 h-px bg-gray-200 mx-2" />
            <div className={`flex items-center gap-2 ${step === 'qr' || step === 'verify' ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'qr' || step === 'verify' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100'}`}>
                2
              </div>
              <span className="text-sm font-medium">Scan QR</span>
            </div>
            <div className="flex-1 h-px bg-gray-200 mx-2" />
            <div className={`flex items-center gap-2 ${step === 'complete' ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'complete' ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100'}`}>
                3
              </div>
              <span className="text-sm font-medium">Complete</span>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Password Verification */}
          {step === 'password' && (
            <form onSubmit={handleInitiate2FA} className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Step 1: Verify Your Identity</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your password to begin 2FA setup
                </p>
                
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                {onCancel && (
                  <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={loading || !password} className="flex-1">
                  {loading ? 'Verifying...' : 'Continue'}
                </Button>
              </div>
            </form>
          )}

          {/* Step 2: QR Code Display */}
          {step === 'qr' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Step 2: Scan QR Code</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Use an authenticator app (Google Authenticator, Authy, 1Password, etc.) to scan this QR code
                </p>

                {/* QR Code */}
                <div className="flex justify-center p-6 bg-white border-2 border-gray-200 rounded-lg">
                  <img src={qrCode} alt="2FA QR Code" className="w-64 h-64" />
                </div>

                {/* Manual Entry */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Can't scan? Enter this key manually:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white border rounded text-sm font-mono">
                      {manualKey}
                    </code>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={copyManualKey}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Backup Codes */}
                {backupCodes.length > 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-yellow-900">Save Your Backup Codes</p>
                        <p className="text-sm text-yellow-800">
                          Store these codes safely. Each can be used once if you lose access to your authenticator.
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {backupCodes.map((code, idx) => (
                        <code key={idx} className="px-3 py-2 bg-white border rounded text-sm font-mono">
                          {code}
                        </code>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline"
                        onClick={copyBackupCodes}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Codes
                      </Button>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline"
                        onClick={downloadBackupCodes}
                        className="flex-1"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Button 
                onClick={() => setStep('verify')} 
                className="w-full"
                disabled={!savedBackupCodes}
              >
                {savedBackupCodes ? 'Continue to Verification' : 'Save Backup Codes First'}
              </Button>
            </div>
          )}

          {/* Step 3: Verification */}
          {step === 'verify' && (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Step 3: Verify Setup</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter the 6-digit code from your authenticator app
                </p>
                
                <Input
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setStep('qr')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || verificationCode.length !== 6} 
                  className="flex-1"
                >
                  {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
                </Button>
              </div>
            </form>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                2FA Enabled Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                Your account is now protected with two-factor authentication.
              </p>
              <Alert className="bg-emerald-50 border-emerald-200">
                <Lock className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800">
                  From now on, you'll need your authenticator app code to sign in.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorAuthSetup;
